import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import StructureImage from './StructureImage';

// Zone colors matching 2D view
const ZONE_COLORS: Record<number, number> = {
  1: 0xdc2626, // Red - Literal Match
  2: 0xf97316, // Orange - Chemical Isostere
  3: 0xeab308, // Yellow - Biological Isostere
  4: 0xfacc15, // Light Yellow - Close Analog
  5: 0x22c55e, // Green - Novel
};

const ZONE_CONFIG: Record<number, { label: string; description: string; risk: string }> = {
  1: { label: 'Literal Match', description: 'Exact InChI match', risk: 'Definite infringement' },
  2: { label: 'Chemical Isostere', description: 'F↔Cl, CH₃↔CF₃', risk: 'Likely infringement' },
  3: { label: 'Biological Isostere', description: 'COOH↔tetrazole', risk: 'Possible infringement' },
  4: { label: 'Close Analog', description: 'High MCS similarity', risk: 'Obviousness risk' },
  5: { label: 'Novel', description: 'Outside patent scope', risk: 'Freedom to operate' },
};

interface MoleculeData {
  id: number;
  smiles: string;
  name?: string;
  inchi?: string;
  ip_status?: string | null;
  patent_count_exact?: number;
  patent_count_similar?: number;
  max_similarity?: number | null;
  closest_patent?: string | null;
  enriched_at?: string | null;
  zone: number;
  patent_matches?: any[];
  top_patent_matches?: any[];
  isostere_type?: 'chemical' | 'biological' | null;
  isostere_name?: string | null;
  transform_smirks?: string | null;
  matched_smiles?: string | null;
  mcs_atoms?: number | null;
  mcs_fraction?: number | null;
}

interface PatentSummary {
  patent_number: string;
  patent_title: string;
  match_count: number;
  patent_id?: string;
  matched_compounds?: number;
  total_scope?: number;
  scope_normalized?: number;
}

interface MapData {
  molecules: any[];
  patents: PatentSummary[];
  summary: {
    total_molecules: number;
    enriched_molecules: number;
    zone_counts: Record<string, number>;
    total_patents: number;
  };
}

interface PatentCollisionMap3DProps {
  mapData: MapData;
  onMoleculeSelect?: (molecule: any) => void;
  selectedMolecule?: any;
  pinnedMolecules?: Map<number, MoleculeData>;
  onPinnedChange?: (pinned: Map<number, MoleculeData>) => void;
  visibleZones?: number[];
  collisionViewMode?: 'my-molecules' | 'patent-landscape';
  displayLimit?: number;
}

// Hash function for consistent positioning
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash);
};

const seededRandom = (seed: number): number => {
  const x = Math.sin(seed * 9999) * 10000;
  return x - Math.floor(x);
};

// Easing functions removed — all elements appear instantly

// Default visible zones - defined outside component to maintain stable reference
const DEFAULT_VISIBLE_ZONES = [1, 2, 3, 4, 5];

const PatentCollisionMap3D: React.FC<PatentCollisionMap3DProps> = ({
  mapData,
  onMoleculeSelect,
  selectedMolecule,
  pinnedMolecules: parentPinned,
  onPinnedChange,
  visibleZones = DEFAULT_VISIBLE_ZONES,
  collisionViewMode = 'my-molecules',
  displayLimit,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const moleculeMeshesRef = useRef<Map<number, THREE.Mesh>>(new Map());
  const patentMeshesRef = useRef<Map<string, THREE.Mesh>>(new Map());
  const connectionLinesRef = useRef<THREE.Line[]>([]);
  const labelsGroupRef = useRef<THREE.Group | null>(null);
  const raycasterRef = useRef<THREE.Raycaster>(new THREE.Raycaster());
  const mouseRef = useRef<THREE.Vector2>(new THREE.Vector2());
  const animationFrameRef = useRef<number | null>(null);
  const hasUserInteractedRef = useRef<boolean>(false);
  const [manualRotate, setManualRotate] = useState(false);
  const [hoveredMolecule, setHoveredMolecule] = useState<MoleculeData | null>(null);
  const [animationComplete, setAnimationComplete] = useState(true);
  const [globeLimit, setGlobeLimit] = useState(displayLimit ?? 200);
  const [webGLError, setWebGLError] = useState<string | null>(null);

  // Reactive dark mode — triggers scene updates on theme toggle
  const [isDarkMode, setIsDarkMode] = useState(() => document.body.classList.contains('theme-dark'));
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains('theme-dark'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Update scene background + stars on theme change (lightweight — no full rebuild)
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    scene.background = new THREE.Color(isDarkMode ? 0x0a0a0f : 0xf8fafc);
    // Update star material color
    scene.children.forEach(child => {
      if (child instanceof THREE.Points) {
        (child.material as THREE.PointsMaterial).color.set(isDarkMode ? 0x444466 : 0xd0d4dc);
      }
    });
  }, [isDarkMode]);

  // Sync globeLimit when parent's displayLimit prop changes
  useEffect(() => {
    if (displayLimit != null) {
      setGlobeLimit(displayLimit);
    }
  }, [displayLimit]);
  // Use parent's pinned state if provided, otherwise fall back to local
  const [localPinned, setLocalPinned] = useState<Map<number, MoleculeData>>(new Map());
  const pinnedMolecules = parentPinned ?? localPinned;
  const setPinnedMolecules = useCallback((updater: Map<number, MoleculeData> | ((prev: Map<number, MoleculeData>) => Map<number, MoleculeData>)) => {
    if (onPinnedChange) {
      if (typeof updater === 'function') {
        onPinnedChange(updater(parentPinned ?? new Map()));
      } else {
        onPinnedChange(updater);
      }
    } else {
      setLocalPinned(updater as any);
    }
  }, [onPinnedChange, parentPinned]);

  // detailMolecule derived from parent's selectedMolecule (for highlight/legend effects)
  const detailMolecule = selectedMolecule || null;

  const [legendCollapsed, setLegendCollapsed] = useState(false);
  const [showExplanation, setShowExplanation] = useState(() => {
    const saved = localStorage.getItem('3d-explanation-visible');
    return saved === null ? true : saved === 'true';
  });
  // clickTimerRef removed — single-click handling only, no delay needed
  const updatePinnedRef = useRef<(() => void) | null>(null);

  // Save explanation preference
  useEffect(() => {
    localStorage.setItem('3d-explanation-visible', String(showExplanation));
  }, [showExplanation]);

  // Initialize Three.js scene
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Scene background - theme-aware (updated reactively via isDarkMode effect)
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(isDarkMode ? 0x0a0a0f : 0xf8fafc);

    // Add starfield effect
    const starGeometry = new THREE.BufferGeometry();
    const starCount = 500;
    const starPositions = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount * 3; i += 3) {
      starPositions[i] = (Math.random() - 0.5) * 300;
      starPositions[i + 1] = (Math.random() - 0.5) * 300;
      starPositions[i + 2] = (Math.random() - 0.5) * 300;
    }
    starGeometry.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));
    const starMaterial = new THREE.PointsMaterial({ color: isDarkMode ? 0x444466 : 0xd0d4dc, size: 0.5 });
    const stars = new THREE.Points(starGeometry, starMaterial);
    scene.add(stars);

    sceneRef.current = scene;

    // Camera - placed at final position immediately
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.set(0, 0, 60);
    cameraRef.current = camera;

    // Renderer — wrapped in try/catch for WebGL unavailability
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true });
    } catch (e) {
      setWebGLError("3D view unavailable — your browser doesn't support WebGL");
      return;
    }
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Listen for WebGL context loss (GPU memory pressure from AG Grid / Plotly / Ketcher)
    const handleContextLost = (e: Event) => {
      e.preventDefault();
      setWebGLError("3D view lost — switch to 2D Scatter for a stable view");
    };
    renderer.domElement.addEventListener('webglcontextlost', handleContextLost);

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.rotateSpeed = 0.4;    // Slower rotation (default 1.0)
    controls.zoomSpeed = 0.6;      // Slower zoom
    controls.panSpeed = 0.5;       // Slower pan
    controls.enablePan = true;     // Allow dragging globe back
    controls.minDistance = 5;
    controls.maxDistance = 50;
    controls.maxPolarAngle = Math.PI * 0.85;
    controls.minPolarAngle = Math.PI * 0.15;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.3;
    controlsRef.current = controls;

    // Stop auto-rotate permanently on user interaction
    const stopAutoRotate = () => {
      hasUserInteractedRef.current = true;
      controls.autoRotate = false;
    };
    renderer.domElement.addEventListener('pointerdown', stopAutoRotate);
    renderer.domElement.addEventListener('wheel', stopAutoRotate);

    // Auto-stop rotation after 3 seconds (eye-catcher only)
    const autoStopTimer = setTimeout(() => {
      if (!hasUserInteractedRef.current) {
        controls.autoRotate = false;
      }
    }, 3000);

    // Ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    // Multiple directional lights for depth
    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.6);
    directionalLight1.position.set(50, 50, 50);
    scene.add(directionalLight1);

    const directionalLight2 = new THREE.DirectionalLight(0x6366f1, 0.3);
    directionalLight2.position.set(-50, -20, -30);
    scene.add(directionalLight2);

    // Labels group
    const labelsGroup = new THREE.Group();
    scene.add(labelsGroup);
    labelsGroupRef.current = labelsGroup;

    // All elements appear at full scale/opacity instantly — no phased animation.
    // Set meshes to final scale, labels to final opacity, lines to final dash.
    moleculeMeshesRef.current.forEach((mesh) => {
      mesh.scale.setScalar(1);
    });
    patentMeshesRef.current.forEach((mesh) => {
      const targetScale = mesh.userData.scaleTarget || 0.6;
      mesh.scale.setScalar(targetScale);
    });
    connectionLinesRef.current.forEach((line) => {
      const material = line.material as THREE.LineDashedMaterial;
      material.dashSize = 1;
      material.gapSize = 0.3;
    });
    if (labelsGroupRef.current) {
      labelsGroupRef.current.traverse((child) => {
        if (child instanceof THREE.Sprite) {
          (child.material as THREE.SpriteMaterial).opacity = 0.9;
        }
      });
    }

    // Simple render loop — no animation phases
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      // Update controls first so camera position is current
      controls.update();

      // Update floating pinned structure positions using the new camera position
      updatePinnedRef.current?.();

      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;
      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, newHeight);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      clearTimeout(autoStopTimer);
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      window.removeEventListener('resize', handleResize);
      renderer.domElement.removeEventListener('pointerdown', stopAutoRotate);
      renderer.domElement.removeEventListener('wheel', stopAutoRotate);

      // Dispose controls
      controls.dispose();

      // Dispose all geometries and materials
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh || object instanceof THREE.Line || object instanceof THREE.Points) {
          object.geometry?.dispose();
          if (Array.isArray(object.material)) {
            object.material.forEach(m => m.dispose());
          } else if (object.material) {
            object.material.dispose();
          }
        }
        if (object instanceof THREE.Sprite) {
          object.material?.dispose();
          (object.material as THREE.SpriteMaterial)?.map?.dispose();
        }
      });

      // Clear refs (don't clear parent pinned/selected state — it persists across view switches)
      moleculeMeshesRef.current.clear();
      patentMeshesRef.current.clear();
      connectionLinesRef.current = [];

      // Dispose renderer and free GPU memory
      renderer.domElement.removeEventListener('webglcontextlost', handleContextLost);
      renderer.dispose();
      renderer.forceContextLoss();

      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  // Sync manual rotate toggle with controls
  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.autoRotate = manualRotate;
      controlsRef.current.autoRotateSpeed = 0.3;
    }
  }, [manualRotate]);

  // Create molecules, patent compounds, and connections
  useEffect(() => {
    if (!sceneRef.current || !mapData || !labelsGroupRef.current) return;

    const scene = sceneRef.current;
    const labelsGroup = labelsGroupRef.current;

    // Clear existing objects
    moleculeMeshesRef.current.forEach((mesh) => {
      mesh.geometry?.dispose();
      (mesh.material as THREE.Material)?.dispose();
      scene.remove(mesh);
    });
    moleculeMeshesRef.current.clear();

    patentMeshesRef.current.forEach((mesh) => {
      mesh.geometry?.dispose();
      (mesh.material as THREE.Material)?.dispose();
      scene.remove(mesh);
    });
    patentMeshesRef.current.clear();

    connectionLinesRef.current.forEach((line) => {
      line.geometry?.dispose();
      (line.material as THREE.Material)?.dispose();
      scene.remove(line);
    });
    connectionLinesRef.current = [];

    // Clear labels
    while (labelsGroup.children.length > 0) {
      const child = labelsGroup.children[0];
      if (child instanceof THREE.Sprite) {
        child.material?.dispose();
        (child.material as THREE.SpriteMaterial)?.map?.dispose();
      }
      labelsGroup.remove(child);
    }

    // Remove old patent territories
    scene.children
      .filter((child) => child.userData.isPatentTerritory)
      .forEach((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry?.dispose();
          (child.material as THREE.Material)?.dispose();
        }
        scene.remove(child);
      });

    const allVisibleMolecules = (mapData.molecules || []).filter(mol => visibleZones.includes(mol.zone));
    // Sample molecules for performance — prioritise at-risk zones (lower zone number first)
    let molecules = allVisibleMolecules;
    if (allVisibleMolecules.length > globeLimit) {
      const sorted = [...allVisibleMolecules].sort((a, b) => a.zone - b.zone);
      molecules = sorted.slice(0, globeLimit);
    }
    const patents = mapData.patents || [];

    // Track patent positions for later
    const patentPositions: Map<string, THREE.Vector3> = new Map();
    const moleculePositions: Map<number, THREE.Vector3> = new Map();

    // Collect unique patent compounds from all molecule matches (shared by both modes)
    const patentCompounds: Map<string, { patentNumber: string; smiles?: string; molecules: number[] }> = new Map();

    molecules.forEach((mol) => {
      const matches = mol.top_patent_matches || mol.patent_matches || [];
      matches.forEach((pm: any) => {
        const patentId = pm.surechembl_id || pm.patent_number;
        if (!patentId) return;
        const compoundKey = pm.matched_compound || patentId;
        if (!patentCompounds.has(compoundKey)) {
          patentCompounds.set(compoundKey, {
            patentNumber: patentId,
            smiles: pm.matched_smiles,
            molecules: [],
          });
        }
        patentCompounds.get(compoundKey)!.molecules.push(mol.id);
      });
    });

    if (collisionViewMode === 'my-molecules') {
      // ══ MY MOLECULES → PATENTS MODE ══
      // Molecules are large coloured spheres, patents are small grey spheres

      const sphereGeometry = new THREE.SphereGeometry(0.6, 24, 24);

      molecules.forEach((mol, index) => {
        const hash1 = hashString(mol.smiles || `mol-${mol.id}`);
        const hash2 = hashString(`${mol.smiles}-y` || `mol-${mol.id}-y`);
        const hash3 = hashString(`${mol.smiles}-z` || `mol-${mol.id}-z`);

        const zoneRadiusMap: Record<number, number> = {
          1: 15, 2: 25, 3: 35, 4: 45, 5: 55,
        };
        const zoneRadius = zoneRadiusMap[mol.zone] || 40;

        const theta = seededRandom(hash1 + index * 7) * Math.PI * 2;
        const phi = Math.acos(2 * seededRandom(hash2 + index * 13) - 1);
        const r = zoneRadius * (0.7 + seededRandom(hash3 + index * 17) * 0.6);

        const x = r * Math.sin(phi) * Math.cos(theta);
        const y = r * Math.sin(phi) * Math.sin(theta);
        const z = r * Math.cos(phi);

        const color = ZONE_COLORS[mol.zone] || 0x22c55e;
        const material = new THREE.MeshPhongMaterial({
          color, shininess: 100, emissive: color, emissiveIntensity: 0.3,
          transparent: true, opacity: 0.95,
        });

        const mesh = new THREE.Mesh(sphereGeometry, material);
        mesh.position.set(x, y, z);
        mesh.scale.setScalar(1);
        mesh.userData = { moleculeId: mol.id, molecule: mol, type: 'molecule' };

        scene.add(mesh);
        moleculeMeshesRef.current.set(mol.id, mesh);
        moleculePositions.set(mol.id, new THREE.Vector3(x, y, z));
      });

      // Patent compound spheres (smaller, grey)
      const patentSphereGeometry = new THREE.SphereGeometry(0.35, 16, 16);
      const patentMaterial = new THREE.MeshPhongMaterial({
        color: 0x64748b, shininess: 60, emissive: 0x334155, emissiveIntensity: 0.2,
        transparent: true, opacity: 0.85,
      });

      patentCompounds.forEach((compound, compoundKey) => {
        const connectedPositions = compound.molecules
          .map(id => moleculePositions.get(id))
          .filter(Boolean) as THREE.Vector3[];
        if (connectedPositions.length === 0) return;

        const centroid = new THREE.Vector3();
        connectedPositions.forEach(p => centroid.add(p));
        centroid.divideScalar(connectedPositions.length);

        const hash = hashString(compoundKey);
        const offset = new THREE.Vector3(
          (seededRandom(hash) - 0.5) * 8,
          (seededRandom(hash + 1) - 0.5) * 8,
          (seededRandom(hash + 2) - 0.5) * 8
        );
        const position = centroid.add(offset);

        const mesh = new THREE.Mesh(patentSphereGeometry, patentMaterial.clone());
        mesh.position.copy(position);
        mesh.scale.setScalar(0.6);
        mesh.userData = { patentNumber: compound.patentNumber, compoundKey, type: 'patent' };

        scene.add(mesh);
        patentMeshesRef.current.set(compoundKey, mesh);
        patentPositions.set(compoundKey, position.clone());

        // Label sprite
        const labelId = compound.patentNumber.includes('SCHEMBL')
          ? compound.patentNumber
          : `SCHEMBL${compoundKey.slice(0, 8)}`;

        const canvas2d = document.createElement('canvas');
        const context = canvas2d.getContext('2d')!;
        canvas2d.width = 256;
        canvas2d.height = 64;
        context.fillStyle = 'rgba(0, 0, 0, 0)';
        context.fillRect(0, 0, canvas2d.width, canvas2d.height);
        context.font = 'bold 24px Inter, system-ui, sans-serif';
        context.fillStyle = isDarkMode ? '#94a3b8' : '#475569';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(labelId.slice(0, 15), canvas2d.width / 2, canvas2d.height / 2);

        const texture = new THREE.CanvasTexture(canvas2d);
        const spriteMaterial = new THREE.SpriteMaterial({ map: texture, transparent: true, opacity: 0.9 });
        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.copy(position);
        sprite.position.y += 1.2;
        sprite.scale.set(4, 1, 1);
        labelsGroup.add(sprite);
      });

      // Connection lines: molecule → patent only
      molecules.forEach((mol) => {
        const molPos = moleculePositions.get(mol.id);
        if (!molPos) return;

        const matches = mol.top_patent_matches || mol.patent_matches || [];
        matches.forEach((pm: any) => {
          const patentId = pm.surechembl_id || pm.patent_number;
          const compoundKey = pm.matched_compound || patentId;
          const patentPos = patentPositions.get(compoundKey);
          if (!patentPos) return;

          const lineColor = ZONE_COLORS[mol.zone] || 0x475569;
          const lineMatCopy = new THREE.LineDashedMaterial({
            color: lineColor, dashSize: 1, gapSize: 0.3, transparent: true, opacity: 0.5,
          });

          const geometry = new THREE.BufferGeometry().setFromPoints([molPos, patentPos]);
          const line = new THREE.Line(geometry, lineMatCopy);
          line.computeLineDistances();
          scene.add(line);
          connectionLinesRef.current.push(line);
        });
      });

    } else {
      // ══ PATENT LANDSCAPE MODE ══
      // Patent families are large coloured spheres, molecules are small markers

      // Group by patent_number to form families
      const patentFamilies3D = new Map<string, {
        patentNumber: string;
        moleculeIds: Set<number>;
        zones: number[];
        compoundKeys: string[];
      }>();

      molecules.forEach((mol) => {
        const matches = mol.top_patent_matches || mol.patent_matches || [];
        matches.forEach((pm: any) => {
          const patentNum = pm.patent_number || pm.surechembl_id || '';
          if (!patentNum) return;
          if (!patentFamilies3D.has(patentNum)) {
            patentFamilies3D.set(patentNum, {
              patentNumber: patentNum,
              moleculeIds: new Set(),
              zones: [],
              compoundKeys: [],
            });
          }
          const fam = patentFamilies3D.get(patentNum)!;
          fam.moleculeIds.add(mol.id);
          fam.zones.push(mol.zone);
          const ck = pm.matched_compound || pm.surechembl_id || patentNum;
          if (!fam.compoundKeys.includes(ck)) fam.compoundKeys.push(ck);
        });
      });

      // Create patent family spheres (large, colored by risk zone)
      const familySphereGeometry = new THREE.SphereGeometry(1.0, 32, 32);
      const zoneColorMap3D: Record<number, number> = {
        1: 0xdc2626, 2: 0xea580c, 3: 0xca8a04, 4: 0x2563eb, 5: 0x16a34a,
      };
      const familyPositions3D = new Map<string, THREE.Vector3>();

      let familyIdx = 0;
      patentFamilies3D.forEach((family, patentNum) => {
        const compoundsInFamily = Math.max(1, family.compoundKeys.length);
        const scaleSize = 0.5 + 0.4 * Math.log2(Math.max(1, compoundsInFamily));

        // Position families in a spread-out shell
        const hash1 = hashString(patentNum);
        const hash2 = hashString(patentNum + '-y');
        const hash3 = hashString(patentNum + '-z');
        const theta = seededRandom(hash1 + familyIdx * 7) * Math.PI * 2;
        const phi = Math.acos(2 * seededRandom(hash2 + familyIdx * 13) - 1);
        const shellRadius = 30 + seededRandom(hash3 + familyIdx * 17) * 20;

        const x = shellRadius * Math.sin(phi) * Math.cos(theta);
        const y = shellRadius * Math.sin(phi) * Math.sin(theta);
        const z = shellRadius * Math.cos(phi);

        const minZone = family.zones.length > 0 ? Math.min(...family.zones) : 0;
        const color = minZone > 0 ? (zoneColorMap3D[minZone] || 0x9ca3af) : 0x9ca3af;

        const material = new THREE.MeshPhongMaterial({
          color, shininess: 80, emissive: color, emissiveIntensity: 0.25,
          transparent: true, opacity: 0.8,
        });

        const mesh = new THREE.Mesh(familySphereGeometry, material);
        mesh.position.set(x, y, z);
        mesh.scale.setScalar(scaleSize);
        mesh.userData = { patentNumber: patentNum, compoundKey: patentNum, type: 'patent', scaleTarget: scaleSize };

        scene.add(mesh);
        patentMeshesRef.current.set(patentNum, mesh);
        patentPositions.set(patentNum, new THREE.Vector3(x, y, z));
        familyPositions3D.set(patentNum, new THREE.Vector3(x, y, z));

        // Label sprite with patent number
        const labelText = patentNum.includes('SCHEMBL') ? 'Cluster' : patentNum;
        const canvas2d = document.createElement('canvas');
        const context = canvas2d.getContext('2d')!;
        canvas2d.width = 256;
        canvas2d.height = 64;
        context.fillStyle = 'rgba(0, 0, 0, 0)';
        context.fillRect(0, 0, canvas2d.width, canvas2d.height);
        context.font = 'bold 22px Inter, system-ui, sans-serif';
        context.fillStyle = isDarkMode ? '#e2e8f0' : '#1e293b';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(labelText.slice(0, 15), canvas2d.width / 2, canvas2d.height / 2);

        const texture = new THREE.CanvasTexture(canvas2d);
        const spriteMat = new THREE.SpriteMaterial({ map: texture, transparent: true, opacity: 0.9 });
        const sprite = new THREE.Sprite(spriteMat);
        sprite.position.set(x, y + 2.5, z);
        sprite.scale.set(5, 1.2, 1);
        labelsGroup.add(sprite);

        familyIdx++;
      });

      // Create molecule spheres (small bright markers)
      const smallSphereGeometry = new THREE.SphereGeometry(0.3, 16, 16);

      molecules.forEach((mol, index) => {
        const hash1 = hashString(mol.smiles || `mol-${mol.id}`);
        const hash2 = hashString(`${mol.smiles}-y` || `mol-${mol.id}-y`);
        const hash3 = hashString(`${mol.smiles}-z` || `mol-${mol.id}-z`);

        // Position molecules near the patent families they match
        const matches = mol.top_patent_matches || mol.patent_matches || [];
        let targetPos = new THREE.Vector3(0, 0, 0);
        let foundTarget = false;

        if (matches.length > 0) {
          const pm = matches[0];
          const familyKey = pm.patent_number || pm.surechembl_id || '';
          const familyPos = familyPositions3D.get(familyKey);
          if (familyPos) {
            targetPos = familyPos.clone();
            foundTarget = true;
          }
        }

        if (!foundTarget) {
          // Position in outer shell if no family match
          const theta = seededRandom(hash1 + index * 7) * Math.PI * 2;
          const phi = Math.acos(2 * seededRandom(hash2 + index * 13) - 1);
          const r = 60 + seededRandom(hash3 + index * 17) * 15;
          targetPos.set(
            r * Math.sin(phi) * Math.cos(theta),
            r * Math.sin(phi) * Math.sin(theta),
            r * Math.cos(phi)
          );
        } else {
          // Offset from family centroid
          const hash = hashString(`${mol.id}-offset`);
          targetPos.add(new THREE.Vector3(
            (seededRandom(hash) - 0.5) * 10,
            (seededRandom(hash + 1) - 0.5) * 10,
            (seededRandom(hash + 2) - 0.5) * 10
          ));
        }

        const color = ZONE_COLORS[mol.zone] || 0x22c55e;
        const material = new THREE.MeshPhongMaterial({
          color, shininess: 120, emissive: 0xffffff, emissiveIntensity: 0.15,
          transparent: true, opacity: 0.95,
        });

        const mesh = new THREE.Mesh(smallSphereGeometry, material);
        mesh.position.copy(targetPos);
        mesh.scale.setScalar(1);
        mesh.userData = { moleculeId: mol.id, molecule: mol, type: 'molecule' };

        scene.add(mesh);
        moleculeMeshesRef.current.set(mol.id, mesh);
        moleculePositions.set(mol.id, targetPos.clone());
      });

      // Patent-to-patent connection lines (families sharing molecules)
      const familyKeys = Array.from(patentFamilies3D.keys());
      for (let i = 0; i < familyKeys.length; i++) {
        for (let j = i + 1; j < familyKeys.length; j++) {
          const famA = patentFamilies3D.get(familyKeys[i])!;
          const famB = patentFamilies3D.get(familyKeys[j])!;

          let sharedCount = 0;
          famA.moleculeIds.forEach(id => { if (famB.moleculeIds.has(id)) sharedCount++; });
          if (sharedCount === 0) continue;

          const posA = familyPositions3D.get(familyKeys[i]);
          const posB = familyPositions3D.get(familyKeys[j]);
          if (!posA || !posB) continue;

          const lineMat = new THREE.LineDashedMaterial({
            color: 0x94a3b8, dashSize: 1, gapSize: 0.3,
            transparent: true, opacity: Math.min(0.6, 0.2 + sharedCount * 0.1),
          });

          const geometry = new THREE.BufferGeometry().setFromPoints([posA, posB]);
          const line = new THREE.Line(geometry, lineMat);
          line.computeLineDistances();
          scene.add(line);
          connectionLinesRef.current.push(line);
        }
      }
    }

    // Create patent territory spheres
    const patentMoleculePositions: Map<string, THREE.Vector3[]> = new Map();

    molecules.forEach((mol) => {
      const molPos = moleculePositions.get(mol.id);
      if (!molPos) return;

      const matches = mol.top_patent_matches || mol.patent_matches || [];
      matches.forEach((pm: any) => {
        const patentId = pm.surechembl_id || pm.patent_number;
        if (!patentId) return;
        if (!patentMoleculePositions.has(patentId)) {
          patentMoleculePositions.set(patentId, []);
        }
        patentMoleculePositions.get(patentId)!.push(molPos.clone());
      });
    });

    patentMoleculePositions.forEach((positions, patentNumber) => {
      if (positions.length < 2) return;

      const centroid = new THREE.Vector3();
      positions.forEach((p) => centroid.add(p));
      centroid.divideScalar(positions.length);

      let maxDist = 0;
      positions.forEach((p) => {
        const dist = p.distanceTo(centroid);
        if (dist > maxDist) maxDist = dist;
      });

      const patentData = patents.find((p: any) =>
        p.patent_id === patentNumber || p.patent_number === patentNumber
      );
      const scopeNormalized = patentData?.scope_normalized || 0;
      const scopeMultiplier = 1 + scopeNormalized * 0.8;
      const radius = (maxDist + 4) * scopeMultiplier;

      const dangerColor = new THREE.Color(0xef4444);
      const safeColor = new THREE.Color(0x475569);
      const color = safeColor.clone().lerp(dangerColor, scopeNormalized * 0.5);

      // Dashed wireframe sphere for territory
      const geometry = new THREE.SphereGeometry(radius, 24, 24);
      const edges = new THREE.EdgesGeometry(geometry);
      const material = new THREE.LineDashedMaterial({
        color: color,
        dashSize: 2,
        gapSize: 1,
        transparent: true,
        opacity: 0.2 + scopeNormalized * 0.15,
      });
      const wireframe = new THREE.LineSegments(edges, material);
      wireframe.computeLineDistances();
      wireframe.position.copy(centroid);
      wireframe.userData = {
        isPatentTerritory: true,
        patentNumber,
        scope: patentData?.total_scope || 0,
        scopeNormalized,
      };
      scene.add(wireframe);
    });

    // Auto-center camera to fit all data points
    if (cameraRef.current && controlsRef.current && moleculeMeshesRef.current.size > 0) {
      const bbox = new THREE.Box3();
      moleculeMeshesRef.current.forEach(mesh => {
        bbox.expandByPoint(mesh.position);
      });
      const center = new THREE.Vector3();
      bbox.getCenter(center);
      const bsize = new THREE.Vector3();
      bbox.getSize(bsize);
      const maxDim = Math.max(bsize.x, bsize.y, bsize.z);
      const fov = cameraRef.current.fov * (Math.PI / 180);
      const fitDist = (maxDim / 2) / Math.tan(fov / 2) * 1.5;
      cameraRef.current.position.set(center.x, center.y, center.z + fitDist);
      controlsRef.current.target.copy(center);
      controlsRef.current.maxDistance = Math.max(50, fitDist * 2);
      controlsRef.current.minDistance = Math.max(5, fitDist * 0.15);
      controlsRef.current.update();
    }

    // Scene rebuilt — all elements are already at full scale/opacity

  }, [mapData, visibleZones, collisionViewMode, globeLimit, isDarkMode]);

  // Highlight pinned / detail molecules + auto-collapse legend
  useEffect(() => {
    moleculeMeshesRef.current.forEach((mesh, id) => {
      const isPinned = pinnedMolecules.has(id);
      const isDetail = detailMolecule?.id === id;
      const targetScale = isDetail ? 1.5 : isPinned ? 1.3 : 1;

      if (animationComplete) {
        mesh.scale.setScalar(targetScale);
      }

      const material = mesh.material as THREE.MeshPhongMaterial;
      material.emissiveIntensity = isDetail ? 0.6 : isPinned ? 0.5 : 0.3;
    });
    setLegendCollapsed(!!detailMolecule);
  }, [pinnedMolecules, detailMolecule, animationComplete]);

  // Update floating pin positions every frame via ref callback
  useEffect(() => {
    updatePinnedRef.current = () => {
      const camera = cameraRef.current;
      const container = containerRef.current;
      if (!camera || !container || pinnedMolecules.size === 0) return;
      const w = container.clientWidth;
      const h = container.clientHeight;

      pinnedMolecules.forEach((_mol, id) => {
        const mesh = moleculeMeshesRef.current.get(id);
        if (!mesh) return;
        const pos = mesh.position.clone().project(camera);
        const sx = ((pos.x + 1) / 2) * w;
        const sy = ((-pos.y + 1) / 2) * h;

        // Position structure to upper-right of sphere
        const ox = sx + 20;
        const oy = sy - 200;

        const el = document.getElementById(`pin-${id}`);
        const ln = document.getElementById(`pin-line-${id}`);
        if (el) {
          el.style.left = `${ox}px`;
          el.style.top = `${oy}px`;
        }
        if (ln) {
          ln.setAttribute('x1', String(sx));
          ln.setAttribute('y1', String(sy));
          ln.setAttribute('x2', String(ox + 5));
          ln.setAttribute('y2', String(oy + 185));
        }
      });
    };
  }, [pinnedMolecules]);

  // Handle mouse move for hover
  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (!containerRef.current || !cameraRef.current || !sceneRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
    const meshes = Array.from(moleculeMeshesRef.current.values());
    const intersects = raycasterRef.current.intersectObjects(meshes);

    if (intersects.length > 0) {
      const mol = intersects[0].object.userData.molecule as MoleculeData;
      setHoveredMolecule(mol);
      containerRef.current.style.cursor = 'pointer';
    } else {
      setHoveredMolecule(null);
      containerRef.current.style.cursor = 'grab';
    }
  }, []);

  // Raycast helper — returns the hit molecule or null
  const raycastMolecule = useCallback((event: React.MouseEvent): MoleculeData | null => {
    if (!containerRef.current || !cameraRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
    const meshes = Array.from(moleculeMeshesRef.current.values());
    const intersects = raycasterRef.current.intersectObjects(meshes);
    return intersects.length > 0 ? (intersects[0].object.userData.molecule as MoleculeData) : null;
  }, []);

  // Click: toggle pin + set as active (matching 2D Scatter behaviour)
  const handleClick = useCallback((event: React.MouseEvent) => {
    const mol = raycastMolecule(event);
    if (mol) {
      // Toggle pin
      setPinnedMolecules(prev => {
        const next = new Map(prev);
        if (next.has(mol.id)) {
          next.delete(mol.id);
        } else {
          next.set(mol.id, mol);
        }
        return next;
      });
      // Always set as active
      onMoleculeSelect?.(mol);
    }
  }, [raycastMolecule, onMoleculeSelect, setPinnedMolecules]);

  // Double-click: same as click (pin + select) — no separate behaviour needed
  const handleDoubleClick = useCallback((event: React.MouseEvent) => {
    // Handled by single click
  }, []);

  // Calculate zone counts directly from molecules (more reliable than summary)
  const zoneCounts = useMemo(() => {
    const counts: Record<string, number> = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 };
    mapData?.molecules?.forEach((mol: any) => {
      const zone = String(mol.zone || 5);
      counts[zone] = (counts[zone] || 0) + 1;
    });
    return counts;
  }, [mapData?.molecules]);

  const totalMolecules = mapData?.molecules?.length || 0;

  // WebGL error fallback
  if (webGLError) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-900/50 rounded-xl">
        <div className="text-center px-8 py-12">
          <svg className="w-12 h-12 mx-auto mb-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h3 className="text-lg font-semibold text-white mb-2">WebGL Unavailable</h3>
          <p className="text-slate-400 text-sm max-w-md">{webGLError}</p>
        </div>
      </div>
    );
  }

  return (
      <div
        ref={containerRef}
        className="flex-1 relative w-full h-full"
        onMouseMove={handleMouseMove}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        style={{ cursor: 'grab' }}
      >
        {/* Hover tooltip */}
        {hoveredMolecule && (
          <div className="absolute top-4 left-4 backdrop-blur rounded-lg px-4 py-3 shadow-2xl z-10 pointer-events-none" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', opacity: 0.95 }}>
            <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              {hoveredMolecule.name || `Molecule ${hoveredMolecule.id}`}
            </div>
            <div className="text-xs mt-1.5 flex items-center gap-2">
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: `#${ZONE_COLORS[hoveredMolecule.zone].toString(16).padStart(6, '0')}` }}
              />
              <span style={{ color: `#${ZONE_COLORS[hoveredMolecule.zone].toString(16).padStart(6, '0')}` }}>
                {ZONE_CONFIG[hoveredMolecule.zone]?.label}
              </span>
            </div>
            {((hoveredMolecule.patent_count_exact || 0) > 0 || (hoveredMolecule.patent_count_similar || 0) > 0) && (
              <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                {(hoveredMolecule.patent_count_exact || 0) > 0 && `${hoveredMolecule.patent_count_exact} exact`}
                {(hoveredMolecule.patent_count_exact || 0) > 0 && (hoveredMolecule.patent_count_similar || 0) > 0 && ' · '}
                {(hoveredMolecule.patent_count_similar || 0) > 0 && `${hoveredMolecule.patent_count_similar} similar`}
                <span className="ml-1 text-slate-500">in SureChEMBL</span>
              </div>
            )}
          </div>
        )}

        {/* Animation phase indicator */}
        {!animationComplete && (
          <div className="absolute top-4 right-4 backdrop-blur rounded-lg px-3 py-2 text-xs" style={{ backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)', opacity: 0.8 }}>
            Loading 3D view...
          </div>
        )}

        {/* Info toggle - top left */}
        {!hoveredMolecule && (
          <button
            onClick={() => setShowExplanation(!showExplanation)}
            className={`absolute top-4 left-4 w-8 h-8 rounded-full flex items-center justify-center transition-all z-20 ${
              showExplanation
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                : 'bg-slate-700/80 text-slate-300 hover:bg-slate-600 hover:text-white'
            }`}
            title={showExplanation ? 'Hide detailed guide' : 'Show detailed guide'}
          >
            <span className="text-sm font-bold">?</span>
          </button>
        )}

        {/* Rotate toggle button - below info button */}
        {animationComplete && (
          <button
            onClick={() => setManualRotate(!manualRotate)}
            className={`absolute top-14 left-4 w-8 h-8 rounded-full flex items-center justify-center transition-all z-20 ${
              manualRotate
                ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/30'
                : 'bg-slate-700/80 text-slate-300 hover:bg-slate-600 hover:text-white'
            }`}
            title={manualRotate ? 'Stop rotation' : 'Auto-rotate'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
            </svg>
          </button>
        )}

        {/* Reset View button — bottom-right corner */}
        {animationComplete && (
          <button
            onClick={() => {
              if (cameraRef.current && controlsRef.current && moleculeMeshesRef.current.size > 0) {
                const bbox = new THREE.Box3();
                moleculeMeshesRef.current.forEach(mesh => bbox.expandByPoint(mesh.position));
                const center = new THREE.Vector3();
                bbox.getCenter(center);
                const bsize = new THREE.Vector3();
                bbox.getSize(bsize);
                const maxDim = Math.max(bsize.x, bsize.y, bsize.z);
                const fov = cameraRef.current.fov * (Math.PI / 180);
                const fitDist = (maxDim / 2) / Math.tan(fov / 2) * 1.5;
                cameraRef.current.position.set(center.x, center.y, center.z + fitDist);
                controlsRef.current.target.copy(center);
                controlsRef.current.update();
              }
            }}
            className="absolute bottom-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all z-20 bg-slate-700/80 text-slate-300 hover:bg-slate-600 hover:text-white"
            title="Reset view"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
          </button>
        )}

        {/* Sampling indicator + controls hint */}
        <div className="absolute bottom-4 left-4 flex flex-col gap-1.5 z-10">
          {(() => {
            const totalVisible = (mapData.molecules || []).filter(mol => visibleZones.includes(mol.zone)).length;
            const shown = Math.min(globeLimit, totalVisible);
            if (totalVisible > shown) {
              return (
                <div className="backdrop-blur rounded px-2 py-1" style={{ backgroundColor: 'var(--card-bg)', opacity: 0.9 }}>
                  <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    Showing {shown} of {totalVisible} molecules
                  </span>
                </div>
              );
            }
            return null;
          })()}
          <div className="text-xs pointer-events-none backdrop-blur rounded px-2 py-1" style={{ color: 'var(--text-muted)', backgroundColor: 'var(--card-bg)', opacity: 0.7 }}>
            Drag to rotate • Scroll to zoom • Right-click to pan
          </div>
        </div>

        {/* SVG overlay for connecting lines from spheres to floating structures */}
        {pinnedMolecules.size > 0 && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none z-10" style={{ overflow: 'visible' }}>
            {Array.from(pinnedMolecules.entries()).map(([id, mol]) => (
              <line
                key={id}
                id={`pin-line-${id}`}
                x1="0" y1="0" x2="0" y2="0"
                stroke={`#${ZONE_COLORS[mol.zone].toString(16).padStart(6, '0')}`}
                strokeWidth="1.5"
                strokeOpacity="0.5"
              />
            ))}
          </svg>
        )}

        {/* Floating structure images — one per pinned molecule */}
        {Array.from(pinnedMolecules.entries()).map(([id, mol]) => {
          const isActive = selectedMolecule?.id === id;
          return (
          <div
            key={id}
            id={`pin-${id}`}
            className="absolute z-20"
            style={{ left: -9999, top: -9999, pointerEvents: 'auto' }}
          >
            <div
              className="relative cursor-pointer group"
              style={{ width: 180 }}
              onClick={(e) => {
                e.stopPropagation();
                onMoleculeSelect?.(mol);
              }}
            >
              {/* Dismiss button — visible on hover */}
              <button
                className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-slate-700/80 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setPinnedMolecules(prev => { const n = new Map(prev); n.delete(id); return n; });
                  if (selectedMolecule?.id === id) onMoleculeSelect?.(null);
                }}
              >
                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div style={{
                filter: isActive ? 'drop-shadow(0 0 8px rgba(20,184,166,0.7))' : 'drop-shadow(0 1px 4px rgba(0,0,0,0.5))',
              }}>
                <StructureImage smiles={mol.smiles} size={180} />
              </div>
              <div style={{ fontSize: 11, textAlign: 'center', marginTop: 2, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textShadow: isDarkMode ? '0 1px 3px rgba(0,0,0,0.8)' : '0 1px 2px rgba(255,255,255,0.8)' }}>
                {mol.name || `Mol ${mol.id}`}
              </div>
            </div>
          </div>
          );
        })}
      </div>

  );
};

export default PatentCollisionMap3D;
